/**
 * 20260929 - "IS THE LAPTOP SPLIT IN EFFECT RIGHT NOW?" — one flag, one writer.
 *
 * Owner, 20260929: "on the Timeline tab in laptop mode can you make the Script/Comments panels fullscreen?
 * They're staying as panels in this view which makes them appear too small to use".
 *
 * The split's OWNER is SceneBoardPage: it alone knows the full rule (the laptop pose resolved by
 * hooks/useLaptopSplit.ts, the Timeline route, and the collapse under fullscreen / the AI workspace /
 * the Audio workspace). It publishes the answer twice, from one layout effect:
 *   - the `laptop-split` class on <html>, which the panels' stylesheets key their fullscreen overlay on
 *     (the same shape as the M key's `force-mobile` class);
 *   - this flag, for the JS half of the same decision (the ScriptPanel / CommentPanel portal to <body>,
 *     the CommentPanel's close button, TopHeader's auto-open suggestion) — so no component re-derives
 *     the collapse rules and drifts from them.
 *
 * A leaf module (React only, no store imports) so any component can read it without a cycle. Nothing
 * but SceneBoardPage writes it; everything else only reads.
 */
import { useSyncExternalStore } from 'react';

let active = false;
const listeners = new Set<() => void>();

/** The split's owner only (SceneBoardPage). Idempotent: an unchanged value notifies nobody. */
export function setLaptopSplitActive(next: boolean): void {
  if (active === next) return;
  active = next;
  for (const listener of [...listeners]) listener();
}

/** The current answer, read imperatively (an effect that must not depend on it, e.g. TopHeader's). */
export function isLaptopSplitActive(): boolean {
  return active;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** True while the laptop split is in effect on the Timeline tab; false on every other host and pose. */
export function useLaptopSplitActive(): boolean {
  return useSyncExternalStore(subscribe, isLaptopSplitActive, () => false);
}
